import { useState } from 'react';
import { Link } from 'react-router-dom';

const APStatsStudyPlan = () => {
  const [activeWeek, setActiveWeek] = useState<number | null>(null);

  const studyPlan = [
    {
      week: 1,
      title: 'Week 1: Exploring One-Variable Data',
      topics: [
        'Representing categorical data with frequency tables, bar charts, pie charts',
        'Creating dotplots, stemplots, and histograms',
        'Measures of center: mean, median, mode',
        'Measures of spread: range, IQR, standard deviation'
      ],
      dailyGoals: [
        'Day 1: Master frequency tables and bar charts (30 practice problems)',
        'Day 2: Create and interpret histograms (20 problems)',
        'Day 3: Calculate mean, median, mode (25 problems)',
        'Day 4: Calculate IQR and standard deviation (20 problems)',
        'Day 5: Boxplots and comparing distributions (25 problems)',
        'Weekend: Review Unit 1, complete 50 mixed problems'
      ]
    },
    {
      week: 2,
      title: 'Week 2: Exploring Two-Variable Data',
      topics: [
        'Creating and interpreting scatterplots',
        'Calculating correlation coefficient',
        'Finding least-squares regression line',
        'Residuals and residual plots'
      ],
      dailyGoals: [
        'Day 1: Scatterplots and association (20 problems)',
        'Day 2: Calculate and interpret correlation (25 problems)',
        'Day 3: Find LSRL equations (20 problems)',
        'Day 4: Calculate residuals and use residual plots (25 problems)',
        'Day 5: R-squared and model interpretation (20 problems)',
        'Weekend: Review Unit 2, complete 40 mixed problems'
      ]
    },
    {
      week: 3,
      title: 'Week 3: Collecting Data',
      topics: [
        'Simple random sampling and sampling methods',
        'Sources of bias in sampling',
        'Experimental design principles',
        'Blocking and matched pairs designs'
      ],
      dailyGoals: [
        'Day 1: SRS and stratified sampling (20 problems)',
        'Day 2: Identify bias in sampling (25 problems)',
        'Day 3: Design experiments with randomization (20 problems)',
        'Day 4: Blocking and matched pairs (20 problems)',
        'Day 5: Confounding and lurking variables (25 problems)',
        'Weekend: Review Unit 3, complete 40 mixed problems'
      ]
    },
    {
      week: 4,
      title: 'Week 4: Probability Fundamentals',
      topics: [
        'Basic probability rules and complement',
        'Conditional probability and independence',
        'Two-way tables and tree diagrams',
        'Addition and multiplication rules'
      ],
      dailyGoals: [
        'Day 1: Basic probability rules (30 problems)',
        'Day 2: Conditional probability (25 problems)',
        'Day 3: Independence vs disjoint events (20 problems)',
        'Day 4: Two-way tables (25 problems)',
        'Day 5: Complex probability problems (20 problems)',
        'Weekend: Review probability, complete 50 mixed problems'
      ]
    },
    {
      week: 5,
      title: 'Week 5: Random Variables & Distributions',
      topics: [
        'Expected value and variance',
        'Combining random variables',
        'Normal distribution and z-scores',
        'Binomial and geometric distributions'
      ],
      dailyGoals: [
        'Day 1: Expected value and variance (25 problems)',
        'Day 2: Combining random variables (20 problems)',
        'Day 3: Normal distribution calculations (30 problems)',
        'Day 4: Binomial distribution (25 problems)',
        'Day 5: Geometric distribution (20 problems)',
        'Weekend: Review Unit 4, complete 50 mixed problems'
      ]
    },
    {
      week: 6,
      title: 'Week 6: Sampling Distributions',
      topics: [
        'Understanding sampling variability',
        'Sampling distribution of sample proportion',
        'Sampling distribution of sample mean',
        'Central Limit Theorem applications'
      ],
      dailyGoals: [
        'Day 1: Sampling variability concepts (15 problems)',
        'Day 2: Sampling distribution of p̂ (25 problems)',
        'Day 3: Sampling distribution of x̄ (25 problems)',
        'Day 4: Central Limit Theorem (20 problems)',
        'Day 5: Mixed sampling distributions (25 problems)',
        'Weekend: Review Unit 5, complete 40 mixed problems'
      ]
    },
    {
      week: 7,
      title: 'Week 7: Inference for Proportions',
      topics: [
        'One-sample confidence intervals for proportions',
        'Two-sample confidence intervals for proportions',
        'One-sample z-tests for proportions',
        'Two-sample z-tests for proportions'
      ],
      dailyGoals: [
        'Day 1: One-sample CI for proportion (25 problems)',
        'Day 2: Two-sample CI for proportions (20 problems)',
        'Day 3: One-sample z-test (25 problems)',
        'Day 4: Two-sample z-test (20 problems)',
        'Day 5: Type I and Type II errors (20 problems)',
        'Weekend: Review Unit 6, complete 45 mixed problems'
      ]
    },
    {
      week: 8,
      title: 'Week 8: Inference for Means',
      topics: [
        'One-sample t-intervals and t-tests',
        'Two-sample t-procedures',
        'Paired t-procedures',
        't-distribution properties'
      ],
      dailyGoals: [
        'Day 1: One-sample t-interval (25 problems)',
        'Day 2: One-sample t-test (25 problems)',
        'Day 3: Two-sample t-procedures (20 problems)',
        'Day 4: Paired t-procedures (25 problems)',
        'Day 5: Mixed inference for means (25 problems)',
        'Weekend: Review Unit 7, complete 50 mixed problems'
      ]
    },
    {
      week: 9,
      title: 'Week 9: Chi-Square Tests',
      topics: [
        'Chi-square goodness-of-fit test',
        'Chi-square test for independence',
        'Chi-square test for homogeneity',
        'Conditions and calculations'
      ],
      dailyGoals: [
        'Day 1: Goodness-of-fit test (20 problems)',
        'Day 2: Test for independence (20 problems)',
        'Day 3: Test for homogeneity (20 problems)',
        'Day 4: Distinguish between chi-square tests (15 problems)',
        'Day 5: Mixed chi-square problems (25 problems)',
        'Weekend: Review Unit 8, complete 35 mixed problems'
      ]
    },
    {
      week: 10,
      title: 'Week 10: Inference for Regression',
      topics: [
        'Conditions for regression inference (LINE)',
        't-test for slope',
        'Confidence interval for slope',
        'Interpreting computer output'
      ],
      dailyGoals: [
        'Day 1: Check LINE conditions (15 problems)',
        'Day 2: t-test for slope (20 problems)',
        'Day 3: CI for slope (20 problems)',
        'Day 4: Interpret regression output (20 problems)',
        'Day 5: Mixed regression inference (25 problems)',
        'Weekend: Review Unit 9, complete 35 mixed problems'
      ]
    },
    {
      week: 11,
      title: 'Week 11: Comprehensive Review I',
      topics: [
        'Units 1-4 comprehensive review',
        'Exploratory data analysis',
        'Study design and probability',
        'Practice AP-style free response'
      ],
      dailyGoals: [
        'Day 1: Units 1-2 review (40 problems)',
        'Day 2: Unit 3 review (30 problems)',
        'Day 3: Unit 4 review (40 problems)',
        'Day 4: Mixed Units 1-4 (50 problems)',
        'Day 5: AP-style FRQs from Units 1-4 (3 complete questions)',
        'Weekend: Timed practice test on Units 1-4'
      ]
    },
    {
      week: 12,
      title: 'Week 12: Comprehensive Review II',
      topics: [
        'Units 5-9 comprehensive review',
        'Sampling distributions and inference',
        'All inference procedures',
        'Practice AP-style free response'
      ],
      dailyGoals: [
        'Day 1: Unit 5 review (30 problems)',
        'Day 2: Units 6-7 review (50 problems)',
        'Day 3: Units 8-9 review (40 problems)',
        'Day 4: Mixed inference problems (60 problems)',
        'Day 5: AP-style FRQs from Units 5-9 (4 complete questions)',
        'Weekend: Timed practice test on Units 5-9'
      ]
    },
    {
      week: 13,
      title: 'Week 13: Full Practice Exams',
      topics: [
        'Complete AP Statistics practice exam',
        'Timing strategies',
        'Free response techniques',
        'Weak area reinforcement'
      ],
      dailyGoals: [
        'Day 1: Full practice exam (40 MCQ + 6 FRQ)',
        'Day 2: Review exam, identify weak areas',
        'Day 3: Practice weak areas (50 targeted problems)',
        'Day 4: Second practice exam (40 MCQ + 6 FRQ)',
        'Day 5: Review second exam, final weak area practice',
        'Weekend: Final confidence-building mixed review (75 problems)'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white py-16">
        <div className="max-w-6xl mx-auto px-6">
          <Link 
            to="/ap-statistics" 
            className="inline-flex items-center text-purple-100 hover:text-white mb-6 transition-colors"
          >
            <i className="ri-arrow-left-line mr-2"></i>
            Back to AP Statistics Curriculum
          </Link>
          <h1 className="text-5xl font-bold mb-4">AP Statistics Study Plan</h1>
          <p className="text-xl text-purple-100 max-w-3xl">
            13-week comprehensive study schedule mapping all 9 units to daily practice goals. 
            Stay on track and master every concept before exam day.
          </p>
        </div>
      </div>

      {/* Study Plan Grid */}
      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* Progress Overview */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Your Study Journey</h2>
              <p className="text-gray-600 mt-1">13 weeks • 9 units • 1,000+ practice problems</p>
            </div>
            <Link 
              to="/ap-stats-progress" 
              className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition-shadow"
            >
              <i className="ri-task-line mr-2"></i>
              Track Progress
            </Link>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-purple-50 rounded-lg p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-purple-600 font-semibold">Exploratory Data</span>
                <span className="text-sm text-purple-600">Weeks 1-2</span>
              </div>
              <div className="text-2xl font-bold text-purple-900">Units 1-2</div>
              <p className="text-sm text-purple-700 mt-1">One & two-variable data analysis</p>
            </div>
            
            <div className="bg-pink-50 rounded-lg p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-pink-600 font-semibold">Design & Probability</span>
                <span className="text-sm text-pink-600">Weeks 3-5</span>
              </div>
              <div className="text-2xl font-bold text-pink-900">Units 3-4</div>
              <p className="text-sm text-pink-700 mt-1">Sampling, experiments & distributions</p>
            </div>
            
            <div className="bg-indigo-50 rounded-lg p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-indigo-600 font-semibold">Inference</span>
                <span className="text-sm text-indigo-600">Weeks 6-10</span>
              </div>
              <div className="text-2xl font-bold text-indigo-900">Units 5-9</div>
              <p className="text-sm text-indigo-700 mt-1">Sampling distributions & hypothesis tests</p>
            </div>
          </div>
        </div>

        {/* Weekly Plans */}
        <div className="space-y-4">
          {studyPlan.map((week) => (
            <div key={week.week} className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-shadow">
              <button
                onClick={() => setActiveWeek(activeWeek === week.week ? null : week.week)}
                className="w-full px-8 py-6 flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <div className="flex items-center space-x-4">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-lg">{week.week}</span>
                  </div>
                  <div className="text-left">
                    <h3 className="text-xl font-bold text-gray-900">{week.title}</h3>
                    <p className="text-gray-600 text-sm mt-1">
                      {week.topics.length} topics • {week.dailyGoals.length} days
                    </p>
                  </div>
                </div>
                <i className={`ri-arrow-${activeWeek === week.week ? 'up' : 'down'}-s-line text-2xl text-gray-400`}></i>
              </button>

              {activeWeek === week.week && (
                <div className="px-8 pb-8 pt-4 bg-gradient-to-br from-gray-50 to-white border-t border-gray-100">
                  {/* Topics */}
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <i className="ri-book-open-line mr-2 text-purple-600"></i>
                      Topics Covered
                    </h4>
                    <ul className="space-y-2">
                      {week.topics.map((topic, idx) => (
                        <li key={idx} className="flex items-start text-gray-700">
                          <i className="ri-checkbox-circle-fill text-purple-600 mr-2 mt-1 flex-shrink-0"></i>
                          <span>{topic}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Daily Goals */}
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <i className="ri-calendar-check-line mr-2 text-pink-600"></i>
                      Daily Practice Goals
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {week.dailyGoals.map((goal, idx) => (
                        <div key={idx} className="flex items-start bg-white rounded-lg p-4 shadow-sm">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center flex-shrink-0 mr-3">
                            <span className="text-purple-700 font-semibold text-sm">{idx + 1}</span>
                          </div>
                          <p className="text-gray-700 text-sm leading-relaxed">{goal}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className="mt-12 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl p-8 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Ready to Start Your Journey?</h2>
          <p className="text-purple-100 mb-6 max-w-2xl mx-auto">
            Access the full AP Statistics curriculum with 30 practice questions for each skill, 
            complete with step-by-step solutions and difficulty ratings.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/ap-statistics" 
              className="bg-white text-purple-600 px-8 py-3 rounded-lg font-semibold hover:bg-purple-50 transition-colors"
            >
              View Full Curriculum
            </Link>
            <Link 
              to="/ap-stats-progress" 
              className="bg-purple-800 text-white px-8 py-3 rounded-lg font-semibold hover:bg-purple-900 transition-colors"
            >
              Track Your Progress
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default APStatsStudyPlan;